import { useRef, useState, type DragEvent } from 'react';

import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/Button';

import { formatBytes } from './envio';

/**
 * Area de envio com arrastar e soltar, como a do "Upload de Documento" do OnPoint.
 *
 * O campo de arquivo continua existindo, com rotulo, para quem usa teclado ou leitor
 * de tela: arrastar e um atalho, e nao o unico caminho. O nome e proprio do modulo de
 * documentos, para nao colidir com os componentes comuns de tela.
 */
export function UploadDropzone({
  arquivo,
  onArquivo,
  accept,
  disabled = false,
  label = 'Arquivo do documento',
}: {
  arquivo: File | null;
  onArquivo: (arquivo: File | null) => void;
  /** Tipos de conteudo aceitos, os mesmos que o servidor aceita. */
  accept?: string | undefined;
  disabled?: boolean;
  label?: string;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [sobre, setSobre] = useState(false);

  function soltar(evento: DragEvent<HTMLDivElement>) {
    evento.preventDefault();
    setSobre(false);
    if (disabled) return;
    const solto = evento.dataTransfer.files[0];
    if (solto) onArquivo(solto);
  }

  return (
    <div
      onDragOver={(evento) => {
        evento.preventDefault();
        if (!disabled) setSobre(true);
      }}
      onDragLeave={() => setSobre(false)}
      onDrop={soltar}
      className={cn(
        'flex flex-col items-center gap-3 rounded-md border-2 border-dashed px-4 py-6 text-center',
        'transition-colors duration-(--duration-fast)',
        sobre
          ? 'border-brand-500 bg-brand-50/40'
          : 'border-line-strong bg-surface-sunken',
        disabled ? 'opacity-60' : null,
      )}
    >
      <p className="text-sm text-ink-600">
        Arraste e solte os arquivos aqui para fazer o upload.
      </p>
      <input
        ref={entrada}
        type="file"
        className="sr-only"
        aria-label={label}
        accept={accept}
        disabled={disabled}
        onChange={(evento) => onArquivo(evento.target.files?.[0] ?? null)}
      />
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled}
        onClick={() => entrada.current?.click()}
      >
        Escolher arquivo
      </Button>
      {arquivo ? (
        <p className="text-2xs text-ink-700">
          {arquivo.name} ({formatBytes(arquivo.size)})
        </p>
      ) : null}
    </div>
  );
}
